import { Box, Skeleton } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    getSelectedProperty,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { SupplierActivationStatus } from 'entities/Property/model/types/PropertySchema';
import { getUserRole, isAdminOrManagerRole } from 'entities/User';
import { Vendor, fetchPropertyVendorsList } from 'entities/Vendors';
import { getVendorProductOrderingStatusText } from 'entities/Vendors/model/lib/productSourcingUtil';
import { getIsLoadingVendorsList } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { deleteVendorFromProperty } from 'entities/Vendors/model/services/deleteVendorFromProperty';
import { getRouteVendorsSupplierPage } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { FilterDropDown } from 'shared/ui/FilterDropDown';
import RemoveVendorModal from 'shared/ui/RemoveVendorModal/RemoveVendorModal';
import { SearchField } from 'shared/ui/SearchField/SearchField';
import { VendorsTable } from 'shared/ui/VendorsTable/VendorsTable';

type Indexable = {
    [key: string]: string | Array<string> | boolean;
};

export const PropertyVendorsTable = ({
    vendorsList,
}: {
    vendorsList: Vendor[];
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isLoadingVendors = useSelector(getIsLoadingVendorsList);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedPropertyData = useSelector(getSelectedProperty);
    const [isShowDeleteVendorModal, setIsShowDeleteVendorModal] =
        useState(false);
    const [isLoadingVendor, setIsLoadingVendor] = useState(false);
    const [deleteVendor, setDeleteVendor] = useState<null | Vendor>(null);
    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<string[]>([]);

    const currentUserRole = useSelector(getUserRole);

    const productOrderingStatuses = Object.values(SupplierActivationStatus)
        .map((status) => {
            return {
                label: getVendorProductOrderingStatusText(t, status, '-'),
                value: status,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

    const canManageVendors = isAdminOrManagerRole(currentUserRole);

    const goToSupplierPage = useCallback(
        (supplierId: string) => {
            navigate(
                getRouteVendorsSupplierPage(supplierId, selectedPropertyId),
            );
        },
        [navigate, selectedPropertyId],
    );

    const onOpenDeleteVendor = useCallback(
        (vendor: Vendor) => {
            setDeleteVendor(vendor);
            setIsShowDeleteVendorModal(true);
        },
        [setDeleteVendor, setIsShowDeleteVendorModal],
    );

    const onCloseDeleteVendor = useCallback(() => {
        setIsShowDeleteVendorModal(false);
        setDeleteVendor(null);
    }, []);

    const onDeleteVendor = useCallback(
        async (id: string) => {
            setIsLoadingVendor(true);
            await dispatch(
                deleteVendorFromProperty({
                    propertyId: selectedPropertyData?.id,
                    vendorId: id,
                }),
            );
            onCloseDeleteVendor();
            setIsLoadingVendor(false);
            await dispatch(fetchPropertyVendorsList(selectedPropertyData?.id));
        },
        [dispatch, selectedPropertyData, onCloseDeleteVendor],
    );

    const searchable = [
        'title',
        'email',
        'primaryContact',
        'phone',
        'speciality',
    ];

    const filteredVendors = vendorsList?.filter((vendor: Vendor) => {
        if (selectedStatus.length > 0) {
            const isValidStatus = selectedStatus.includes(
                vendor.operationsAccountStatus,
            );

            if (!isValidStatus) {
                return false;
            }
        }

        const index: Indexable = Object.keys(vendor)
            .filter((key) => searchable.includes(key))
            .reduce((values: Indexable, key: string) => {
                values[key] = vendor[key as keyof Vendor];
                return values;
            }, {});

        return JSON.stringify(Object.values(index))
            .toLowerCase()
            .includes(search.toLowerCase());
    });

    return (
        <Box sx={{ mt: '20px' }}>
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <SearchField
                    value={search}
                    setValue={setSearch}
                    placeholder={t('Search vendor')}
                    sx={{
                        maxWidth: '350px',
                    }}
                />

                <FilterDropDown
                    id="activation_status"
                    label="Product Ordering"
                    options={productOrderingStatuses}
                    value={selectedStatus}
                    setValue={setSelectedStatus}
                />
            </Box>
            <VendorsTable
                vendorsList={filteredVendors}
                isLoadingVendors={isLoadingVendors}
                isMainVendorsPage
                onClickRow={goToSupplierPage}
                onDeleteVendor={canManageVendors ? onOpenDeleteVendor : null}
                deleteVendorId={deleteVendor?.id}
            />
            {isLoadingVendors &&
                Array.from({ length: 10 }).map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rectangular"
                        height="50px"
                        sx={{
                            marginBottom: '2px',
                            width: '100%',
                        }}
                    />
                ))}
            <RemoveVendorModal
                isShowDeleteVendor={isShowDeleteVendorModal}
                title={
                    deleteVendor?.sellerType === 'seller'
                        ? 'Remove Supplier'
                        : 'Remove Service Provider'
                }
                buttonText={
                    deleteVendor?.sellerType === 'seller'
                        ? 'Remove supplier'
                        : 'Remove service provider'
                }
                vendorType={deleteVendor?.sellerType}
                vendorName={deleteVendor?.title}
                propertyName={selectedPropertyData?.propertyNameLegal}
                onCloseDeleteVendor={onCloseDeleteVendor}
                onSubmitDeleteVendor={() => onDeleteVendor(deleteVendor?.id)}
                isLoadingDeleteVendor={isLoadingVendor}
            />
        </Box>
    );
};
